/**
 * ProjectWizardModal — orchestrator del wizard de creación.
 *
 * Composición:
 *  - Header (eyebrow + título + close)
 *  - Stepper (5 pasos)
 *  - Cuerpo: render del step activo (1..5)
 *  - Footer: Cancelar | (Atrás) | Siguiente / Crear
 *
 * Submit pipeline (al final del paso 5):
 *  1. Validar campos requeridos (name, lifecycle, methodology, emails de
 *     stakeholders).
 *  2. POST /api/v1/projects con `code` (auto si vacío), name, methodology,
 *     content_language, client_name (opcional), `creator_role` y
 *     `metadata` que carga el resto de la intención del wizard. El backend
 *     da de alta al usuario actual como líder en esa misma transacción
 *     (OT-0065): por eso el resto de las altas ya pasa su guard de líder.
 *  3. POST /api/v1/projects/{id}/members → uno por stakeholder válido, en
 *     paralelo y esperados. Si alguno falla NO revertimos el proyecto: se
 *     avisa cuáles y por qué, y el tab Equipo permite reintentar.
 *  4. onCreated(projectId) → el caller navega al overview.
 *
 * Errores: cualquier ApiError del POST /projects detiene el flujo y se
 * muestra en el footer. Los fallos de stakeholders se muestran en un toast
 * fijo y no bloquean la navegación.
 */

import { useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react'

import { ApiError } from '../../../api/client'
import { createProject, type CreateProjectBody } from '../../../api/projects'
import { useAccessToken } from '../../../auth/useAccessToken'
import { useMe } from '../../../auth/useMe'
import { useApiErrorMessage } from '../../../hooks/useApiErrorMessage'
import { useLifecycleRoles } from '../../../hooks/useLifecycleRoles'
import { useToastStore } from '../../../stores/toastStore'
import { persistStakeholders } from './persistStakeholders'
import { Step1Template } from './Step1Template'
import { Step2Methodology } from './Step2Methodology'
import { Step3Phases } from './Step3Phases'
import { Step4Team } from './Step4Team'
import { Step5Summary } from './Step5Summary'
import { StepStepper } from './StepStepper'
import {
  buildAutoProjectCode,
  buildInitialState,
  isValidEmail,
  wizardReducer,
  type WizardState,
} from './types'

interface ProjectWizardModalProps {
  open: boolean
  onClose: () => void
  /** Llamado tras un POST /projects exitoso, antes de cerrar el modal.
   * El caller típicamente navega al overview. */
  onCreated: (projectId: string) => void
}

export function ProjectWizardModal({
  open,
  onClose,
  onCreated,
}: ProjectWizardModalProps) {
  const { t, i18n } = useTranslation(['projects', 'errors'])
  const { me } = useMe()
  const { accessToken } = useAccessToken()
  const apiErrorMessage = useApiErrorMessage()
  const pushToast = useToastStore((s) => s.pushToast)

  // Locale inicial = locale del usuario (asumiendo es|en, default 'es').
  const initialLanguage =
    me?.locale === 'en' || me?.locale === 'es' ? me.locale : 'es'
  const [state, dispatch] = useReducer(
    wizardReducer,
    initialLanguage,
    buildInitialState,
  )

  // Roles del ciclo elegido en Step 1 — los consume Step 4 (combo del rol
  // del PM y stakeholders) y Step 5 (resolver el name del template_key).
  const rolesQuery = useLifecycleRoles(state.lifecycleTemplateId)
  const roles = rolesQuery.data ?? []

  if (!open) return null

  function gotoStep(step: WizardState['step']) {
    dispatch({ type: 'set_step', step })
  }

  function next() {
    const error = validateStep(state)
    if (error) {
      dispatch({ type: 'submit_error', message: t(error) })
      return
    }
    if (state.step === 5) {
      void submit()
      return
    }
    dispatch({
      type: 'set_step',
      step: (state.step + 1) as WizardState['step'],
    })
  }

  function back() {
    if (state.step <= 1) return
    dispatch({
      type: 'set_step',
      step: (state.step - 1) as WizardState['step'],
    })
  }

  async function submit() {
    if (!accessToken) {
      dispatch({
        type: 'submit_error',
        message: t('wizard.errors.submit_failed'),
      })
      return
    }
    dispatch({ type: 'submit_start' })

    const body = buildCreateBody(state)

    let projectId: string
    try {
      projectId = (await createProject(accessToken, body)).id
    } catch (err) {
      // Cascada de fallback: traducción frontend del code → mensaje
      // localizado del backend (ApiError.body.message) → genérico.
      // El backend SIEMPRE devuelve detail localizado por Accept-Language
      // (Doc 19 §3.2), así que el segundo nivel rara vez se necesita
      // pero es defensivo cuando el frontend aún no tiene el code en
      // su catálogo (ej. errors recién agregados al backend).
      let message: string = t('wizard.errors.submit_failed')
      if (err instanceof ApiError) {
        const FALLBACK_SENTINEL = '__missing__'
        const translated = t(`errors:${err.code}` as const, {
          defaultValue: FALLBACK_SENTINEL,
        })
        if (translated !== FALLBACK_SENTINEL && translated !== err.code) {
          message = translated
        } else if (err.body?.message) {
          message = err.body.message
        }
      }
      dispatch({ type: 'submit_error', message })
      return
    }

    // El proyecto ya existe y el usuario lo lidera: un stakeholder que
    // falla no lo revierte, pero se dice cuál y por qué.
    const failures = await persistStakeholders(accessToken, projectId, state)
    if (failures.length > 0) {
      pushToast({
        kind: 'critical',
        duration: 0,
        message: t('wizard.errors.stakeholders_partial', {
          count: failures.length,
          list: new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
            failures.map((f) =>
              t('wizard.errors.stakeholder_failure_item', {
                email: f.email,
                reason: apiErrorMessage(f.error),
              }),
            ),
          ),
        }),
      })
    }
    onCreated(projectId)
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/60 p-6"
      role="dialog"
      aria-modal="true"
      aria-label={t('wizard.title')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-surface border border-line rounded-xl w-full max-w-3xl max-h-[92vh] shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-center justify-between shrink-0">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
              {t('wizard.eyebrow')}
            </p>
            <h2 className="font-display text-xl font-semibold text-ink">
              {t('wizard.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-surface-muted text-ink-muted"
            aria-label={t('wizard.actions.cancel')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 border-b border-line bg-surface-muted shrink-0">
          <StepStepper current={state.step} onJumpTo={gotoStep} />
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {state.step === 1 ? (
            <Step1Template
              state={state}
              onPatch={(patch) => dispatch({ type: 'patch', patch })}
              dispatch={dispatch}
            />
          ) : null}
          {state.step === 2 ? (
            <Step2Methodology
              state={state}
              onPatch={(patch) => dispatch({ type: 'patch', patch })}
            />
          ) : null}
          {state.step === 3 ? (
            <Step3Phases
              state={state}
              onSetMode={(code, mode) =>
                dispatch({ type: 'set_phase_mode', code, mode })
              }
            />
          ) : null}
          {state.step === 4 ? (
            <Step4Team
              state={state}
              me={me}
              roles={roles}
              onPatch={(patch) => dispatch({ type: 'patch', patch })}
              onAddStakeholder={(role) => dispatch({ type: 'add_stakeholder', role })}
              onPatchStakeholder={(localId, patch) =>
                dispatch({ type: 'patch_stakeholder', localId, patch })
              }
              onRemoveStakeholder={(localId) =>
                dispatch({ type: 'remove_stakeholder', localId })
              }
            />
          ) : null}
          {state.step === 5 ? (
            <Step5Summary state={state} roles={roles} onJumpTo={gotoStep} />
          ) : null}
        </div>

        {/* Error inline (sobre el footer) */}
        {state.submitError ? (
          <div className="px-6 py-2 border-t border-line bg-critical-soft">
            <p className="font-mono text-xs text-critical">
              {state.submitError}
            </p>
          </div>
        ) : null}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line bg-surface-muted flex items-center justify-between shrink-0">
          <span className="text-xs text-ink-subtle">
            {t('wizard.stepper.of', { current: state.step, total: 5 })}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={state.submitting}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('wizard.actions.cancel')}
            </button>
            {state.step > 1 ? (
              <button
                type="button"
                onClick={back}
                disabled={state.submitting}
                className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md flex items-center gap-1.5 disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('wizard.actions.back')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={next}
              disabled={state.submitting}
              className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md flex items-center gap-2 disabled:opacity-50"
            >
              {state.step === 5 ? (
                <>
                  <Check className="w-4 h-4" />
                  {state.submitting
                    ? t('wizard.actions.creating')
                    : t('wizard.actions.create')}
                </>
              ) : (
                <>
                  {t('wizard.actions.next')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
/** Devuelve la translation key del primer error encontrado. null si OK.
 *
 * Nota: `lifecycleTemplate` y `methodology` tienen default no-null en
 * `buildInitialState`, así que no necesitan checks (TS lo enforce). El
 * único required sin default es `name`. */
function validateStep(state: WizardState): string | null {
  if (state.step === 1) {
    if (!state.name.trim()) return 'projects:wizard.errors.name_required'
    // AI-1.f.bug: lifecycle ahora es required y dinámico — antes tenía
    // default 'hsl' hardcoded. Si el catálogo está vacío o el fetch
    // falla, no se puede avanzar.
    if (!state.lifecycleTemplateId || !state.lifecycleTemplate) {
      return 'projects:wizard.errors.lifecycle_required'
    }
  }
  if (state.step === 4 || state.step === 5) {
    if (!state.internalRole) return 'projects:wizard.errors.internal_role_required'
    for (const stk of state.stakeholders) {
      if (!isValidEmail(stk.email)) {
        return 'projects:wizard.errors.stakeholder_email_invalid'
      }
      if (!stk.role) return 'projects:wizard.errors.stakeholder_role_required'
    }
  }
  return null
}

function buildCreateBody(state: WizardState): CreateProjectBody {
  const code = state.code.trim() || buildAutoProjectCode(state.name, state.client)
  return {
    code,
    name: state.name.trim(),
    methodology: state.methodology,
    content_language: state.contentLanguage,
    client_name: state.client.trim() || undefined,
    // AI-1.f.bug: lifecycle viaja como UUID (no como key en metadata).
    // El service lo lee y materializa snapshot + project_phases +
    // expected_artifacts. metadata sigue llevando la cadencia y
    // overrides del wizard como auditoría.
    lifecycle_template_id: state.lifecycleTemplateId,
    // OT-0065 — con este rol el backend da de alta al usuario actual como
    // líder, en la misma transacción que crea el proyecto.
    creator_role: state.internalRole,
    metadata: {
      wizard_version: 2,
      lifecycle_template_key: state.lifecycleTemplate?.templateKey,
      lifecycle_template_version: state.lifecycleTemplate?.version,
      cadence: {
        sprint_length_weeks: state.sprintLength,
        sprint_start_day: state.sprintStart,
        release_cadence: state.releaseCadence,
      },
      phases: state.phases,
      approvals_template: state.approvalsTemplate,
    },
  }
}
